import { types, getEnv, SnapshotOut, Instance } from 'mobx-state-tree';

import {
    LiveEvents, ProgressStateModelForStore, ProgressStateModel, DesktopLiveEventDetailStore, LiveStream, IGameGroupStore,
    LiveStreamOperatorType, MatchTrackerType, LiveStreamDisciplineType, eventDisciplineWrapper
} from 'eTip--common';
import { ILiveAppEnv } from '../../liveAppEnvironment';
import { SortType, createComparerOfSorting } from 'easit--utils';

export const LiveEventStore = types
    .model({
        id: types.optional(types.number, 0),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        apiData: types.maybeNull(DesktopLiveEventDetailStore),
        dataIsFilled: types.optional(types.boolean, false), // means event exists
        liveStream: types.maybeNull(LiveStream),
        matchTracker: types.maybeNull(LiveStream),
        favoriteGameGroupIds: types.optional(types.map(types.boolean), {}), //expecting always true
        closedGameGroupIds: types.optional(types.map(types.boolean), {}), //expecting always true
        closedGameIds: types.optional(types.map(types.boolean), {}), //expecting always true
        selectedGameGroupTab: types.maybeNull(types.string),
        order: types.maybeNull(types.number),
        expanded: types.maybeNull(types.boolean)
    })
    .views(self => {
        const env = getEnv<ILiveAppEnv>(self);
        let sequenceComparer = createComparerOfSorting((g: IGameGroupStore) => g.sequence, SortType.Asc);

        return {
            isGroupFavorite(id: string): boolean {
                return self.favoriteGameGroupIds.has(id);
            },

            isGameGroupOpen(id: string): boolean {
                return !self.closedGameGroupIds.has(id);
            },

            isGameOpen(id: string): boolean {
                return !self.closedGameIds.has(id.toString());
            },

            get gameGroupByFavorites(): IGameGroupStore[] {
                return self.apiData.gameGroups.sort((g1, g2) => {
                    if (self.favoriteGameGroupIds.has(g1.id)) {
                        if (self.favoriteGameGroupIds.has(g2.id)) {
                            return sequenceComparer(g1, g2);
                        } else {
                            return -1;
                        }
                    } else {
                        if (self.favoriteGameGroupIds.has(g2.id)) {
                            return 1;
                        }
                        return sequenceComparer(g1, g2);
                    }
                });
            },

            get sRNotificationHandler() {
                return env.sRNotificationHandler;
            },

            get userIdForLiveStreamPopup() {
                return env.loggedUserId;
            },

            get matchtrackerKey() {
                return env.settings.matchTrackerKey;
            }
        }
    })
    .actions(self => {
        const env = getEnv<ILiveAppEnv>(self);

        const startForNewEvent = (eventId: number): void => {

            beforeEventChange();

            self.state = ProgressStateModel.Loading;
            self.id = eventId;
            self.dataIsFilled = false;
            self.liveStream = null;
            self.matchTracker = null;
            self.selectedGameGroupTab = null;
            self.favoriteGameGroupIds.clear();
            self.closedGameGroupIds.clear();
            self.closedGameIds.clear();
        };

        const loadFailed = (eventId: number): void => {
            if (self.id === eventId) {
                if (self.state === ProgressStateModel.Loading) {
                    self.state = ProgressStateModel.Failed;
                }
                return;
            }
            env.loggingService.error("loadFailed called for another event, current/called", self.id, eventId);
        };

        const setNotFound = (eventId: number): void => {
            if (self.id === eventId) {
                if (self.state === ProgressStateModel.Loading) {
                    self.state = ProgressStateModel.Success;
                    self.dataIsFilled = false;
                }
                return;
            }
            env.loggingService.error("setNotFound called for another event, current/called", self.id, eventId);
        };

        const setData = (expectedEventId: number, event: LiveEvents.EventsItem | null): void => {

            if (self.id !== expectedEventId) {

                env.loggingService.error("setData called for another event, current/called", self.id, expectedEventId);
                return;
            }

            if (event == null) {
                self.state = ProgressStateModel.Success;
                self.dataIsFilled = false;
                self.selectedGameGroupTab = null;
                self.favoriteGameGroupIds.clear();
                return;
            }

            env.loggingService.info(`live event ${event.ID}`, event);

            self.apiData = DesktopLiveEventDetailStore.create(<SnapshotOut<typeof DesktopLiveEventDetailStore>>{}, env);
            self.apiData.setData(event);

            prepareLiveStream();
            prepareMatchTracker();

            self.state = ProgressStateModel.Success;

            if (!self.dataIsFilled) { //meaning it's the first call of load
                try {
                    loadFavoriteGroups();
                } catch (e) {
                    env.loggingService.error(e);
                }
            }

            env.scoreChangeMonitor.eventUpdated(self.apiData.id, self.apiData.graphicsPresentationId, self.apiData.mainResult);

            if (self.apiData.gameGroups.length > 0) {
                if (self.selectedGameGroupTab === null
                    || !self.apiData.gameGroups.find(g => g.id === self.selectedGameGroupTab)) {
                    self.selectedGameGroupTab = self.gameGroupByFavorites[0].id; //gameGroupByFavorites is view from self.apiData.gameGroups
                }
            }
            self.dataIsFilled = true;

        };

        const clean = (): void => {

            beforeEventChange();

            self.state = ProgressStateModel.NotInitialized;
            self.id = 0;
            self.liveStream = null;
            self.dataIsFilled = false;
        }

        const prepareLiveStream = () => {
            if (self.apiData.liveStream) {
                if (!self.liveStream) {
                    self.liveStream = LiveStream.create(<Instance<typeof LiveStream>>{
                        state: ProgressStateModel.NotInitialized,
                        operator: self.apiData.liveStream.operator,
                        externalId: self.apiData.liveStream.externalId,
                        eventId: self.id,
                        eventName: self.apiData.name,
                        eventDiscipline: eventDisciplineWrapper.isTennis(self.apiData.graphicsPresentationId) ? LiveStreamDisciplineType.Tennis : LiveStreamDisciplineType.Some,
                        eventCategoryPath: self.apiData.categoryPath,
                        eventStart: self.apiData.liveStream.startDate,
                        graphicsPresentationId: self.apiData.graphicsPresentationId
                    }, env);
                } else {
                    self.liveStream.setData({
                        operator: self.apiData.liveStream.operator,
                        externalId: self.apiData.liveStream.externalId,
                        eventId: self.id,
                        eventName: self.apiData.name,
                        eventDiscipline: eventDisciplineWrapper.isTennis(self.apiData.graphicsPresentationId) ? LiveStreamDisciplineType.Tennis : LiveStreamDisciplineType.Some,
                        eventCategoryPath: self.apiData.categoryPath,
                        eventStart: self.apiData.liveStream.startDate,
                        graphicsPresentationId: self.apiData.graphicsPresentationId
                    });
                }
            } else {
                if (self.liveStream) {
                    self.liveStream.videoStoppedForMonitoring();
                }
                self.liveStream = null;
            }
        }

        const prepareMatchTracker = () => {
            if (self.apiData.matchTracker) {

                const operator = self.apiData.matchTracker.type === MatchTrackerType.BRStage ? LiveStreamOperatorType.MatchTrackerBRStage : LiveStreamOperatorType.MatchTrackerBR;

                if (!self.matchTracker) {
                    self.matchTracker = LiveStream.create(<Instance<typeof LiveStream>>{
                        state: ProgressStateModel.NotInitialized,
                        operator: operator,
                        externalId: self.apiData.matchTracker.id,
                        eventId: self.id,
                        eventName: self.apiData.name,
                        eventDiscipline: LiveStreamDisciplineType.None,
                        eventCategoryPath: null,
                        //eventStart: self.apiData.liveStream.startDate,
                        graphicsPresentationId: self.apiData.graphicsPresentationId
                    }, env);
                } else {
                    self.matchTracker.setData({
                        operator: operator,
                        externalId: self.apiData.matchTracker.id,
                        eventId: self.id,
                        eventName: self.apiData.name,
                        eventDiscipline: eventDisciplineWrapper.isTennis(self.apiData.graphicsPresentationId) ? LiveStreamDisciplineType.Tennis : LiveStreamDisciplineType.Some,
                        eventCategoryPath: self.apiData.categoryPath,
                        //eventStart: self.apiData.liveStream.startDate,
                        graphicsPresentationId: self.apiData.graphicsPresentationId
                    });
                }
            } else {
                if (self.matchTracker) {
                    self.matchTracker.videoStoppedForMonitoring();
                }
                self.matchTracker = null;
            }
        }

        const setGameGroupIsFavorite = (gameGroupId: string, value: boolean): void => {
            if (value) {
                self.favoriteGameGroupIds.set(gameGroupId, true);
            } else {
                if (self.favoriteGameGroupIds.has(gameGroupId)) {
                    self.favoriteGameGroupIds.delete(gameGroupId);
                }
            }
            storeFavoriteGroups();
        }

        const storeFavoriteGroups = (): void => {
            let ids: string[] = [];
            self.favoriteGameGroupIds.forEach((_v, key) => ids.push(key))
            env.sessionStorage.setItem(getStoreFavGroupsKey(), JSON.stringify(ids));
        }

        const loadFavoriteGroups = (): void => {
            self.favoriteGameGroupIds.clear();
            let jsonValue = env.sessionStorage.getItem(getStoreFavGroupsKey());
            if (jsonValue) {
                let parsed = JSON.parse(jsonValue);
                if (parsed) {
                    if (parsed instanceof Array) {
                        (<string[]>parsed).forEach(v => {
                            self.favoriteGameGroupIds.set(v, true);
                        })
                    }
                }
            }
        }

        const getStoreFavGroupsKey = (): string => {
            return "liveFavGameGroups_" + self.apiData.graphicsPresentationId;
        }

        const setGameGroupTab = (tabKey: string): void => {
            self.selectedGameGroupTab = tabKey;
        }

        const setGameGroupIsOpen = (id: string, value: boolean): void => {
            if (value) {
                self.closedGameGroupIds.delete(id);
            } else {
                self.closedGameGroupIds.set(id, true);
            }
        }

        const setGameIsOpen = (id: string, value: boolean): void => {
            if (value) {
                self.closedGameIds.delete(id.toString());
            } else {
                self.closedGameIds.set(id.toString(), true);
            }
        }

        const toggleExpand = (): void => {
            self.expanded = !self.expanded;
        }

        const beforeEventChange = () => {
            if (self.state === ProgressStateModel.Success) {
                if (self.liveStream) {
                    self.liveStream.videoStoppedForMonitoring();
                    self.liveStream = null; // or if live in prematch, problem in detail in list
                }
                if (self.matchTracker) {
                    self.matchTracker.videoStoppedForMonitoring();
                    delete self.matchTracker; // or if live in prematch, problem in detail in list
                }
            }
        }

        return {
            clean,
            startForNewEvent,
            setData,
            loadFailed,
            setNotFound,
            setGameGroupIsFavorite,
            setGameGroupIsOpen,
            setGameIsOpen,
            setGameGroupTab,
            toggleExpand
        };
    });

export const getEventDetailParamFromKey = (key: string): { eventId: number; categoryId?: string } => {
    let s = key.split('c');
    if (s.length === 1) {
        return {
            eventId: Number(s[0])
        };
    }

    return {
        eventId: Number(s[0]),
        categoryId: s[1]
    };
};

export type ILiveEventStore = Instance<typeof LiveEventStore>;